/**
 * Frontend API client for the Pipeline page.
 * Maps 1:1 to /v1/observe/pipeline/* and /v1/observe/catalogue/* in
 * backend/product/src/api/server.ts (modules/observe/pipeline.service.ts).
 */

import { request } from './client';

export interface PipelineStatus {
  jev: { enabled: boolean; model: string | null; breakerOpen: boolean; consecutiveFailures: number; openUntil: string | null };
  backup: { tierPin: string | null; models: string[] };
  photos: { url: string | null; reachable: boolean; head: string | null };
  catalogue: { products: number; indexAgeMs: number; parserVersion: number; byCategory: Record<string, number>; hidden: number; manual: number };
}

export interface TierRow { calls: number; ok: number; failed: number; avgLatencyMs: number; costGbp: number }

export interface PipelineSummary {
  hours: number;
  titles: { jev: TierRow; own?: TierRow; backup: TierRow; backupByModel: Record<string, TierRow> };
  identity: { total: number; byReason: Record<string, number>; byCategory: Record<string, Record<string, number>> };
  photos: { judged: number; usable: number; notProduct: number; unsure: number; couldNotAsk: number; noPhoto: number };
  served: { emitServableNow: Record<string, number>; pushes: number; pushesByCategory: Record<string, number> };
}

export interface MissGroup {
  key: string;
  category: string | null;
  listings: number;
  examples: Array<{ title: string; source: string; price: number | null }>;
}

export interface CatalogueRow {
  key: string;
  displayName: string;
  category: string | null;
  source: 'cex' | 'manual' | 'other';
  hidden: boolean;
  lastSeen: string | null;
  boxes: number;
}

export interface AddProductInput {
  displayName: string;
  /** The listing the add started from. Shown as a reference, never used as the name. */
  fromListing?: string;
  category: string;
  brand?: string | null;
  modelLine: string;
  storageGb?: number | null;
  ramGb?: number | null;
}

export async function fetchPipelineStatus(): Promise<PipelineStatus> {
  return (await request<{ ok: true; status: PipelineStatus }>('/v1/observe/pipeline/status')).status;
}

export async function fetchPipelineSummary(hours: number): Promise<PipelineSummary> {
  return (await request<{ ok: true; summary: PipelineSummary }>(`/v1/observe/pipeline/summary?hours=${hours}`)).summary;
}

export interface BrandSuggestions {
  suggestions: Array<{ brand: string; listings: number; examples: string[] }>;
  productsWithoutBrand: number;
}

export async function fetchBrandSuggestions(hours: number): Promise<BrandSuggestions> {
  return (await request<{ ok: true; result: BrandSuggestions }>(`/v1/observe/catalogue/brand-suggestions?hours=${hours}`)).result;
}

export async function fetchCatalogueMisses(hours: number): Promise<MissGroup[]> {
  return (await request<{ ok: true; misses: MissGroup[] }>(`/v1/observe/catalogue/misses?hours=${hours}&limit=40`)).misses;
}

export async function fetchCatalogue(q: { q?: string; category?: string; source?: string; hidden?: boolean }): Promise<{ total: number; rows: CatalogueRow[] }> {
  const p = new URLSearchParams();
  if (q.q) p.set('q', q.q);
  if (q.category) p.set('category', q.category);
  if (q.source) p.set('source', q.source);
  if (q.hidden) p.set('hidden', 'true');
  p.set('limit', '100');
  return request<{ ok: true; total: number; rows: CatalogueRow[] }>(`/v1/observe/catalogue/products?${p.toString()}`);
}

export interface SimilarProduct { key: string; displayName: string; category: string | null }

export async function fetchSimilarProducts(q: string, category: string): Promise<SimilarProduct[]> {
  const p = new URLSearchParams({ q, category });
  return (await request<{ ok: true; products: SimilarProduct[] }>(`/v1/observe/catalogue/similar?${p.toString()}`)).products;
}

export async function addCatalogueProduct(input: AddProductInput): Promise<CatalogueRow> {
  return (await request<{ ok: true; product: CatalogueRow }>('/v1/observe/catalogue/products', { method: 'POST', body: input })).product;
}

export async function setProductHidden(key: string, hidden: boolean): Promise<CatalogueRow> {
  return (await request<{ ok: true; product: CatalogueRow }>(
    `/v1/observe/catalogue/products/${encodeURIComponent(key)}/hidden`, { method: 'POST', body: { hidden } },
  )).product;
}

// ── Delivered ───────────────────────────────────────────────────────────────

export interface Recipient {
  tgId: number | null;
  name: string | null;
  tier: string | null;
  channel: string;
  status: string;
  sentAt: string;
  photoSent: boolean | null;
  dcsAtSend: number | null;
}

/** Why a card went out, recorded when it was sent (not today's re-score). */
export interface WhyAtSend {
  pipelineVersion: number | null;
  categorySource: string | null;
  modelKeySource: string | null;
  textVerdict: {
    by: string | null; category: string | null; productType: string | null;
    isDevice: boolean | null; confidence: number | null; used: boolean;
  } | null;
  identity: {
    reason: string | null; p: number | null; isDevice: number | null; inList: number | null;
    unambiguous: number | null; cached: boolean | null; productCategory: string | null;
  } | null;
  catalogueName: string | null;
  photo: { usable: boolean | null; p: number | null; by: string | null; via: string } | null;
}

export interface DeliveredFlag { reason: string; note: string | null; ts: string }

export const DELIVERED_FLAG_REASONS = [
  ['not-a-device', 'Not a device (accessory, part, game)'],
  ['wrong-product', 'Wrong product (catalogue match)'],
  ['wrong-category', 'Wrong category'],
  ['bad-photo', 'Bad or wrong photo'],
  ['wrong-price', 'Price or saving is wrong'],
  ['wrong-EMIT', 'Should not have gone out (other)'],
] as const;

export interface DeliveredDeal {
  dealId: string;
  name: string;
  title: string;
  image: string | null;
  price: number | null;
  anchorPrice: number | null;
  discountPct: number | null;
  dcsNow: number | null;
  decisionNow: string | null;
  stillServable: boolean;
  category: string | null;
  source: string | null;
  url: string | null;
  modelKey: string | null;
  identityReason: string | null;
  scoredByNewPipeline: boolean;
  firstSent: string;
  lastSent: string;
  sends: number;
  photoSends: number;
  whyAtSend: WhyAtSend | null;
  flags: DeliveredFlag[];
  recipients: Recipient[];
}

export interface DeliveredResult {
  hours: number;
  totals: { sends: number; deals: number; users: number; failed: number; withPhoto: number; withoutPhoto: number; unknownPhoto: number };
  byCategory: Record<string, number>;
  bySource: Record<string, number>;
  byTier: Record<string, number>;
  deals: DeliveredDeal[];
  truncated: boolean;
}

export interface DeliveredFilters {
  hours: number;
  category?: string;
  source?: string;
  tier?: string;
  channel?: string;
  status?: string;
  q?: string;
  tgId?: string;
}

export async function fetchDelivered(f: DeliveredFilters): Promise<DeliveredResult> {
  const p = new URLSearchParams({ hours: String(f.hours), limit: '200' });
  for (const k of ['category', 'source', 'tier', 'channel', 'status', 'q', 'tgId'] as const) {
    const v = f[k];
    if (v) p.set(k, String(v));
  }
  return (await request<{ ok: true; result: DeliveredResult }>(`/v1/observe/delivered?${p.toString()}`)).result;
}

export async function flagDelivered(dealId: string, body: { reason: string; note?: string }) {
  return request<{ ok: true }>(`/v1/observe/delivered/${encodeURIComponent(dealId)}/flag`, { method: 'POST', body });
}
